import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import useAuth from '@/hooks/useAuth';
import { usePlansShouldShowTabs } from '@/hooks/usePlansShouldShowTabs';
import { useScrollMemory } from '@/hooks/useScrollMemory/useScrollMemory';
import {
  AddSortFields,
  addSortFields,
  useSortOptions,
} from '@/hooks/useSortOptions';

import { getNameFromLanguage } from '@/utils/language';
import { isApiError } from '@/utils/request';

import { withRoles } from '@/components/ConfirmationModal/withRoles';
import GenericError from '@/components/Error/GenericError';
import { Fab } from '@/components/Fab/Fab';
import Filter, { FilterDesktop, FilterMobile } from '@/components/Filter';
import { FilterAndSortButtonProps } from '@/components/Filter/components/FilterAndSortButton';
import { FilterSearchBarProps } from '@/components/Filter/components/FilterSearchBar';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';
import LoadMorePaginator from '@/components/LoadMorePaginator/LoadMorePaginator';
import NoMessage from '@/components/NoMessage/NoMessage';
import AddQuizPopUp from '@/components/popups/AddQuizPopUp';
import ConfirmationModal from '@/components/popups/ConfirmationModal';
import SortFilterHeader from '@/components/SortFilterHeader/SortFilterHeader';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import Constants, { routes } from '@/constants';

import { queryClient } from '@/services/api';

import QuizCard from './QuizCard/QuizCard';

import {
  CustomQuizDetailDefinitionDto,
  CustomQuizDto,
  deleteCustomQuiz,
  getCustomQuizDetailsQueryKey,
  getCustomQuizzesOptions,
  getCustomQuizzesQueryKey,
  getOperatorsOptions,
  updateCustomQuiz,
} from '@/_generated/api-clients/xp';
import { sourceDataFamily, useDataView } from '@/atoms/data-view';
import { activeLicenseeLocationAtom } from '@/atoms/licensee-location';
import { quizDraftAtom } from '@/atoms/quiz-draft';

const sourceData = sourceDataFamily('build-quizzes');

const DATAVIEW_KEY = 'manage-quizzes';

const BuildQuizzesTab = () => {
  const { t } = useTranslation();
  const { data: user } = useAuth();

  const userIsStaff = user?.isStaff;
  const isLicenseeUser = user?.isLicensee;
  const activeLicenseeLocation = useAtomValue(activeLicenseeLocationAtom);
  const setQuizDraft = useSetAtom(quizDraftAtom);
  const navigate = useNavigate();
  const userLanguage = user?.language;
  const [quiz, setQuiz] = useState<CustomQuizDetailDefinitionDto>();
  const [showCreateQuizPopUp, setShowCreateQuizPopUp] = useState(false);
  const [showInvalidNameError, setShowInvalidNameError] = useState(false);
  const sortOptions = useSortOptions({ manageQuizzes: true });

  const locationsWithAtLeastLeader =
    user?.allLocationsWithAtLeastLeaderPermissions ?? [];

  const tabsActive = usePlansShouldShowTabs();

  const invalidateQuizzes = (id?: string) => {
    queryClient.invalidateQueries({ queryKey: getCustomQuizzesQueryKey() });
    if (id) {
      queryClient.invalidateQueries({
        queryKey: getCustomQuizDetailsQueryKey({
          path: {
            customQuizId: id,
          },
        }),
      });
    }
  };

  const deleteQuiz = async (id: string, callback?: () => void) => {
    try {
      await deleteCustomQuiz({ path: { customQuizId: id } });
      invalidateQuizzes(id);
      callback?.();
    } catch (error) {
      datadogRum.addError(error);
    }
  };

  const updateQuiz = async (
    body: CustomQuizDetailDefinitionDto,
    callback?: () => void,
  ) => {
    try {
      await updateCustomQuiz({ body });
      invalidateQuizzes(body.quizId);
      callback?.();
    } catch (error) {
      datadogRum.addError(error);
    }
  };

  const {
    data: quizzes,
    isFetching,
    isPending,
    error: errorGettingQuizzes,
  } = useQuery({
    ...getCustomQuizzesOptions(),
  });

  const isFetchingQuizzes = isFetching || isPending;

  useScrollMemory({
    container: 'body',
    key: DATAVIEW_KEY,
    enabled: !isFetchingQuizzes,
  });

  const { data: operators, error: errorGettingOperators } = useQuery({
    ...getOperatorsOptions(),
  });

  const operatorsWhereUserIsLeader = operators?.filter(operator =>
    operator.locations?.some(operatorLocation =>
      locationsWithAtLeastLeader.includes(operatorLocation),
    ),
  );

  const setSource = useSetAtom(sourceDataFamily('manage-quizzes'));

  useEffect(() => {
    setSource((quizzes ?? []).map(addSortFields));
  }, [quizzes, setSource]);

  const [
    {
      display: { items, total },
      filters,
      sort,
    },
    { showMore, setSort, setSearch, resetSearch },
  ] = useDataView<AddSortFields<CustomQuizDto>>(DATAVIEW_KEY);

  const setSourceData = useSetAtom(sourceData);

  useEffect(() => {
    if (isLicenseeUser) {
      const licenseeLocationQuizzes = quizzes?.filter(
        quizToFilter =>
          quizToFilter.operatorId === activeLicenseeLocation?.number,
      );
      setSourceData(licenseeLocationQuizzes ?? []);
    } else {
      setSourceData(quizzes ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLicenseeLocation?.number, isLicenseeUser, quizzes]);

  const isValidQuizName = (quizName: string, operator?: string) => {
    if (!quizName || (!operator && !userIsStaff)) {
      setShowInvalidNameError(true);
      console.error('invalid quiz name or missing operator');
      datadogRum.addError(
        new Error(
          `Build quizzes error - no quiz name or no operator: ${JSON.stringify({
            quizName,
            operator,
          })}`,
        ),
      );
      return false;
    }
    return true;
  };

  const onAddQuiz: React.ComponentProps<
    typeof AddQuizPopUp
  >['submitHandler'] = ({ operator, quizNameInput }) => {
    setShowCreateQuizPopUp(false);
    const trimmedName = quizNameInput.trim();
    if (!isValidQuizName(trimmedName, operator?.id)) {
      return;
    }
    // Clear any in-flight quiz draft, since we are intentionally starting
    // from scratch.
    setQuizDraft(null);
    navigate(
      {
        pathname: routes.plans.managedQuizzesNew,
      },
      {
        state: { operator, quizName: quizNameInput },
      },
    );
  };

  const onRenameQuiz = async (newName: string, quizDetails: CustomQuizDto) => {
    const trimmedName = newName.trim();
    if (!isValidQuizName(trimmedName, quizDetails.operatorId)) {
      return;
    }
    const payload = {
      ...quiz,
      name: {
        ...quiz?.name,
        en: trimmedName,
      },
    } as CustomQuizDetailDefinitionDto;

    updateQuiz(payload, () => {
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id}>
          {`${trimmedName} ${t('TrainingPlans.toastMessage.renamed')}`}
        </ToastMessageBlock>
      ));
    });
  };

  const onSaveTranslations = (
    updatedTranslations: CustomQuizDetailDefinitionDto,
  ) => {
    updateQuiz(updatedTranslations, () => {
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id}>
          {`${getNameFromLanguage(updatedTranslations.name)} ${t(
            'TrainingPlans.translationToastText',
          )} ${
            userLanguage === Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE
              ? Constants.LANGUAGE_OPTIONS.SPANISH
              : Constants.LANGUAGE_OPTIONS.INGLES
          }`}
        </ToastMessageBlock>
      ));
    });
  };

  const onDeleteQuiz = (quizId: string, quizName: string) => {
    deleteQuiz(quizId, () => {
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id}>
          {quizName} {t('TrainingPlans.toastMessage.deleted')}
        </ToastMessageBlock>
      ));
    });
  };

  if (isApiError(errorGettingOperators ?? errorGettingQuizzes)) {
    const error = errorGettingOperators ?? errorGettingQuizzes;
    datadogRum.addError(error);
    return <GenericError />;
  }

  const search: FilterSearchBarProps = {
    onChange: e => {
      setSearch(e.target.value, 'nameLocalLang');
    },
    onClear: () => resetSearch(),
    placeholder: t('TrainingPlans.filtering.searchQuizzes'),
    value: filters.search,
  };

  const filterAndSortButtonOptions: FilterAndSortButtonProps = {
    onSortChange: setSort,
    sortOptions,
    sortValue: sortOptions.find(
      option =>
        option.key === sort?.key && option.direction === sort?.direction,
    ),
    sortOnly: true,
    text: `${t('TrainingPlans.filtering.show')} ${
      total
    } ${t('TrainingPlans.filtering.results')}`,
  };

  const filterOptions = {
    filterAndSortButtonOptions,
    after: <Filter.SearchBar {...search} />,
  };

  const filterOptionsMobile = {
    filterAndSortButtonOptions,
    search: <Filter.SearchBar {...search} />,
  };

  return (
    <>
      <LoadingOverlay isOpen={isFetchingQuizzes} fixed />
      <div className="relative flex max-w-full grow flex-col">
        <div className="flex w-full flex-col">
          {!isFetchingQuizzes && <FilterMobile {...filterOptionsMobile} />}
          {/* Top Row */}
          <div>
            <div className="flex justify-end md:pl-[304px]">
              <SortFilterHeader
                label={t('TrainingPlans.filtering.sortBy')}
                onChange={setSort}
                onClear={() => setSearch('')}
                options={sortOptions}
                showClear={false}
                text={`${total} ${t('TrainingPlans.filtering.plans')}`}
                title={t('TrainingPlans.filtering.manageQuizzes')}
                total={total}
                value={sortOptions.find(
                  option =>
                    option.key === sort?.key &&
                    option.direction === sort?.direction,
                )}
              />
            </div>
            <div className="mt-2.5 flex justify-between">
              {/* Left Side */}
              {!isFetchingQuizzes && (
                <FilterDesktop {...filterOptions} tabsActive={tabsActive} />
              )}

              {/* Right Side */}
              <div className="relative flex max-w-full grow flex-col justify-start gap-4">
                {!isFetchingQuizzes &&
                  items.map(quizDetails => (
                    <QuizCard
                      key={quizDetails.id}
                      onDeleteQuiz={onDeleteQuiz}
                      onRenameQuiz={newName =>
                        onRenameQuiz(newName, quizDetails)
                      }
                      onSaveTranslations={onSaveTranslations}
                      quizDetails={quizDetails}
                      setQuiz={setQuiz}
                    />
                  ))}
                {quizzes?.length === 0 && !isFetchingQuizzes && (
                  <NoMessage
                    message={t('TrainingPlans.buildQuizzes.noQuizzesLoaded')}
                  />
                )}
                <LoadMorePaginator
                  onClick={() => showMore()}
                  showing={items.length}
                  showingText={t('TrainingPlans.showingXOfYQuizzes', {
                    showing: items.length,
                    total,
                  })}
                  total={total}
                />
              </div>
            </div>
          </div>
        </div>

        {(!!operators?.length || !!userIsStaff) && (
          <>
            <Fab
              isOpen={showCreateQuizPopUp}
              onClick={() => {
                // Clear any in-flight quiz draft, since we are intentionally
                // creating a new quiz.
                setQuizDraft(null);
                setShowCreateQuizPopUp(true);
              }}
              tooltip={t('TrainingPlans.buildQuizzes.createQuizHover')}
              icon="quiz-plus"
            />
            <AddQuizPopUp
              isLicenseeUser={!!isLicenseeUser}
              open={showCreateQuizPopUp}
              operators={operatorsWhereUserIsLeader ?? []}
              setOpen={setShowCreateQuizPopUp}
              submitHandler={onAddQuiz}
            />
          </>
        )}
      </div>
      <ConfirmationModal
        bodyText={t('InvalidQuizName.paragraphText')}
        headerText={t('InvalidQuizName.errorHeader')}
        isError={true}
        isOpen={showInvalidNameError}
        onClose={() => setShowInvalidNameError(false)}
        primaryButtonHandler={() => setShowInvalidNameError(false)}
        primaryButtonText={t('Button.close')}
      />
    </>
  );
};

const BuildQuizzesTabWithRoles = withRoles(BuildQuizzesTab, 'quizzes.create');

export default BuildQuizzesTabWithRoles;
